/**
 * InvoicePeek — the Rechnungskontrolle decision peek (Story UI-U4), ported from
 * the prototype invoice-detail-sheet.tsx onto the house DecisionPeek side sheet.
 *
 * Owns its own data (useInvoiceCheckDetail + useInvoiceHistory) and the decision
 * mutation (useInvoiceDecision). Renders SERVER verdicts only — no threshold math,
 * no client re-categorization. Every field the story does not own is feature-
 * detected: absent match/settlement/history → the element hides (defensive AC 5).
 *
 * Decision footer → the as-built E10 endpoints (AC 3 matrix):
 *   checked K0/K1  → handle · in_review K2 → override{K1|K3} · blocked/disputed K3
 *   → override{K3|K1} · matched (unchecked) → orchestrate ("Zuordnung bestätigen").
 * Mandatory reason ≥10 chars mirrors the server 400 (UX nicety; server = truth).
 *
 * [Source: brokly-design-reference/components/brokly/invoice-detail-sheet.tsx]
 * [Source: docs/stories/ui-u4-rechnungskontrolle.md AC2/AC3/AC5]
 */
import { useEffect, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  CalendarClock,
  Check,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  FileWarning,
  ShieldCheck,
  X,
} from 'lucide-react';
import { DecisionPeek } from '../../../components/data/DecisionPeek';
import { Button } from '../../../components/ui/Button';
import { EntityLink } from '../../../components/ui/EntityLink';
import { StatusDot } from '../../../components/ui/StatusDot';
import { cn } from '../../../lib/cn';
import { chf, dateShortDe, dateTimeShortDe } from '../../../lib/format';
import type { DiffCategory } from '@brokly/shared';
import { KategorieBadge } from './KategorieBadge';
import { KonfidenzBadge } from './KonfidenzBadge';
import { useInvoiceCheckDetail } from '../hooks/useInvoiceCheckDetail';
import { useInvoiceHistory } from '../hooks/useInvoiceHistory';
import { useInvoiceDecision, type InvoiceDecision } from '../hooks/useInvoiceDecision';
import {
  KATEGORIE_NAME,
  STATUS_LABEL,
  STATUS_TONE,
  TYP_LABEL,
} from '../api/invoices-api';
import type { InvoiceRow } from '../types';

const MIN_REASON = 10;

/** Server decision_reason provenance prefix that marks a Pflichtregel violation. */
function isPflicht(reason: string | null | undefined): boolean {
  return typeof reason === 'string' && reason.startsWith('Pflicht-mismatch');
}

type FooterMode = 'sealed' | 'handle' | 'k2' | 'k3' | 'none';

function footerMode(row: InvoiceRow): FooterMode {
  const s = row.rawStatus;
  if (s === 'released' || s === 'resolved' || s === 'paid') return 'sealed';
  if (s === 'checked' && (row.kategorie === 'K0' || row.kategorie === 'K1')) return 'handle';
  if (s === 'in_review') return 'k2';
  if (s === 'blocked' || s === 'disputed') return 'k3';
  return 'none'; // received / matched — not yet checked
}

// ── small building blocks ─────────────────────────────────────────────────────

function BigValue({ label, value, tone }: { label: string; value: string; tone?: 'warn' | 'ok' }) {
  return (
    <div className="flex flex-1 flex-col gap-0.5 rounded-xl border border-border bg-card p-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span
        className={cn(
          'mono-num text-lg font-semibold',
          tone === 'warn' && 'text-status-warning',
          tone === 'ok' && 'text-status-success',
        )}
      >
        {value}
      </span>
    </div>
  );
}

type CheckTone = 'ok' | 'warn' | 'fehler';
const checkIcon: Record<CheckTone, ReactNode> = {
  ok: <Check className="size-3" />,
  warn: <AlertTriangle className="size-3" />,
  fehler: <X className="size-3" />,
};
const checkCls: Record<CheckTone, string> = {
  ok: 'bg-status-success-bg text-status-success',
  warn: 'bg-status-warning-bg text-status-warning',
  fehler: 'bg-status-danger-bg text-status-danger',
};

function CheckRow({
  tone,
  label,
  pflicht,
  hint,
}: {
  tone: CheckTone;
  label: string;
  pflicht?: boolean;
  hint?: string;
}) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <span className={cn('mt-0.5 grid size-5 shrink-0 place-items-center rounded-full', checkCls[tone])}>
        {checkIcon[tone]}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-sm font-medium text-foreground">{label}</span>
          {pflicht && (
            <span className="rounded border border-border px-1 text-[10px] font-medium text-muted-foreground">
              Pflicht
            </span>
          )}
        </div>
        {hint && (
          <span
            className={cn(
              'text-xs',
              tone === 'ok'
                ? 'text-muted-foreground'
                : tone === 'warn'
                  ? 'text-status-warning'
                  : 'text-status-danger',
            )}
          >
            {hint}
          </span>
        )}
      </div>
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon?: ReactNode; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5">
        {icon}
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      </div>
      {children}
    </section>
  );
}

// ── settlement (Schlussabrechnung) narrowing ──────────────────────────────────

interface WageCategory {
  category_code: string;
  rate: number;
  wage: number;
  premium: number;
}
function asWageCategories(json: unknown): WageCategory[] {
  if (!Array.isArray(json)) return [];
  return json.filter(
    (c): c is WageCategory =>
      c !== null &&
      typeof c === 'object' &&
      typeof (c as WageCategory).category_code === 'string',
  );
}

// ── the peek ──────────────────────────────────────────────────────────────────

export function InvoicePeek({
  row,
  index,
  total,
  onClose,
  onPrev,
  onNext,
  onToast,
  onError,
  onOpenPolice,
}: {
  row: InvoiceRow;
  index: number;
  total: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  onToast: (message: string) => void;
  onError: (message: string) => void;
  /** Navigate to the matched policy detail (owned by the screen — keeps the peek router-free). */
  onOpenPolice?: (policyId: string) => void;
}) {
  const { data: detail } = useInvoiceCheckDetail(row.id);
  const { data: history } = useInvoiceHistory(row.id);
  const decision = useInvoiceDecision(onError);

  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);

  // Reset the reason on invoice change (prototype behaviour).
  useEffect(() => {
    setReason('');
    setReasonError(false);
  }, [row.id]);

  const check = detail?.latest_check ?? null;
  const match = detail?.match ?? null;
  const settlement = detail?.settlement ?? null;

  const kategorie: DiffCategory | null = row.kategorie ?? check?.category ?? null;
  const pflicht = isPflicht(check?.decision_reason);
  const confidence = match?.confidence != null ? Math.round(Number(match.confidence) * 100) : null;
  const mode = footerMode(row);
  const toleranzOk = kategorie === 'K0' || kategorie === 'K1';

  // Amounts: peek prefers check-detail figures, falls back to the list row.
  const erwartet = check?.expected_premium != null ? Number(check.expected_premium) : row.erwartet;
  const rechnung = check?.invoiced_premium != null ? Number(check.invoiced_premium) : row.rechnung;
  const diff = rechnung - erwartet;

  function submit(d: InvoiceDecision) {
    if (d.kind === 'override' && d.reason.trim().length < MIN_REASON) {
      setReasonError(true);
      return;
    }
    setReasonError(false);
    decision.mutate(
      { id: row.id, decision: d },
      {
        onSuccess: () => {
          onToast('Entscheid gespeichert');
          onNext(); // auto-advance to the next case
        },
      },
    );
  }

  // E = trigger the primary decision (suppressed while typing).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key !== 'e' && e.key !== 'E') return;
      e.preventDefault();
      if (mode === 'handle') submit({ kind: 'handle' });
      else if (mode === 'k2') submit({ kind: 'override', newCategory: 'K1', reason });
      else if (mode === 'k3') submit({ kind: 'override', newCategory: 'K3', reason });
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, reason, row.id]);

  // ── checklist rows (server facts only; absent → hidden) ─────────────────────
  const checks: Array<{ tone: CheckTone; label: string; pflicht?: boolean; hint?: string }> = [];
  checks.push({
    tone: row.versicherer !== '-' ? 'ok' : 'fehler',
    label: 'Versicherer erkannt',
    hint: row.versicherer !== '-' ? undefined : 'Kein Versicherer zugeordnet',
  });
  checks.push(
    row.policyId !== null
      ? {
          tone: confidence !== null && confidence < 70 ? 'warn' : 'ok',
          label: 'Police zugeordnet',
          pflicht: true,
          hint:
            confidence !== null && confidence < 70
              ? `Zuordnung unsicher · Konfidenz ${confidence} %`
              : undefined,
        }
      : { tone: 'fehler', label: 'Police zugeordnet', pflicht: true, hint: 'Nicht zugeordnet' },
  );
  if (kategorie) {
    checks.push({
      tone: kategorie === 'K3' ? 'fehler' : kategorie === 'K2' ? 'warn' : 'ok',
      label: 'Prämienbetrag im Toleranzrahmen',
      hint: kategorie === 'K2' || kategorie === 'K3' ? `Abweichung ${row.pct.toFixed(1)} %` : undefined,
    });
  }
  if (check?.effective_date) {
    checks.push({
      tone: 'ok',
      label: 'Stichtag / Periode plausibel',
      hint: check.prorata_applied ? 'Pro-rata angewendet' : undefined,
    });
  }
  if (pflicht) {
    checks.push({ tone: 'fehler', label: 'Pflichtregel verletzt', pflicht: true });
  }

  const wageCategories = settlement ? asWageCategories(settlement.wage_categories_json) : [];

  // ── decision footer ─────────────────────────────────────────────────────────
  const reasonBox =
    mode === 'k2' || mode === 'k3' ? (
      <div className="flex flex-col gap-1">
        <textarea
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (e.target.value.trim().length >= MIN_REASON) setReasonError(false);
          }}
          rows={2}
          placeholder={
            mode === 'k2'
              ? 'Begründung der Freigabe (Pflicht) …'
              : 'Begründung / Human Override (Pflicht) …'
          }
          className={cn(
            'w-full resize-none rounded-md border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring',
            reasonError ? 'border-status-danger' : 'border-border',
          )}
        />
        {reasonError && (
          <span role="alert" className="text-xs text-status-danger">
            Bitte eine Begründung erfassen (mind. {MIN_REASON} Zeichen).
          </span>
        )}
      </div>
    ) : null;

  const footer =
    mode === 'none' ? null : (
      <div className="flex flex-col gap-2">
        {mode === 'sealed' && (
          <div className="flex items-center gap-2 rounded-md bg-status-success-bg px-3 py-2 text-sm text-status-success">
            <Check className="size-4 shrink-0" />
            Automatisch freigegeben ({kategorie ?? '-'}
            {kategorie ? ` · ${KATEGORIE_NAME[kategorie]}` : ''}).
          </div>
        )}
        {reasonBox}
        <div className="flex flex-wrap items-center gap-2">
          {mode === 'handle' && (
            <Button
              className="bg-status-success text-white hover:bg-status-success/90"
              disabled={decision.isPending}
              onClick={() => submit({ kind: 'handle' })}
            >
              <Check className="size-4" /> Freigeben
            </Button>
          )}
          {mode === 'k2' && (
            <>
              <Button
                disabled={decision.isPending}
                onClick={() => submit({ kind: 'override', newCategory: 'K1', reason })}
              >
                <ShieldCheck className="size-4" /> Prüfen &amp; freigeben
              </Button>
              <div className="ml-auto">
                <Button
                  variant="ghost"
                  className="text-muted-foreground"
                  disabled={decision.isPending}
                  onClick={() => submit({ kind: 'override', newCategory: 'K3', reason })}
                >
                  <X className="size-4" /> Ablehnen
                </Button>
              </div>
            </>
          )}
          {mode === 'k3' && (
            <>
              <Button
                disabled={decision.isPending}
                onClick={() => submit({ kind: 'override', newCategory: 'K3', reason })}
              >
                <FileWarning className="size-4" /> Beanstandung erstellen
              </Button>
              <div className="ml-auto">
                <Button
                  variant="ghost"
                  className="text-muted-foreground"
                  disabled={decision.isPending}
                  onClick={() => submit({ kind: 'override', newCategory: 'K1', reason })}
                >
                  Trotzdem freigeben
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    );

  return (
    <DecisionPeek
      width="w-[672px]"
      title={`Rechnung ${row.nr}`}
      subtitle={`${row.versicherer} · ${row.kunde} · ${TYP_LABEL[row.typ]}`}
      progress={`${index + 1} von ${total}`}
      onClose={onClose}
      onPrev={onPrev}
      onNext={onNext}
      footer={footer}
    >
      <div className="flex flex-col gap-6">
        {/* identity strip */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="mono-num text-sm font-medium">{row.nr}</span>
          {kategorie && <KategorieBadge kategorie={kategorie} pflichtregel={kategorie === 'K3' && pflicht} />}
          <StatusDot tone={STATUS_TONE[row.rawStatus]} label={STATUS_LABEL[row.rawStatus]} />
        </div>

        {/* Soll / Ist + Differenz strip */}
        <div className="flex flex-col gap-3">
          <div className="flex gap-3">
            <BigValue label="Erwartet (Soll)" value={chf(erwartet)} />
            <BigValue label="Rechnung (Ist)" value={chf(rechnung)} tone={toleranzOk ? undefined : 'warn'} />
          </div>
          <div
            className={cn(
              'flex items-center justify-between rounded-xl border px-3 py-2 text-sm',
              toleranzOk
                ? 'border-status-success-border bg-status-success-bg/50'
                : 'border-status-warning-border bg-status-warning-bg/50',
            )}
          >
            <span className="text-muted-foreground">Differenz</span>
            <span
              className={cn(
                'mono-num font-semibold',
                toleranzOk ? 'text-status-success' : 'text-status-warning',
              )}
            >
              {diff >= 0 ? '+' : '−'}
              {chf(Math.abs(diff)).replace('CHF ', '')} · {row.pct.toFixed(1)} %
            </span>
          </div>
        </div>

        {/* Zuordnung zur Police */}
        <Section title="Zuordnung zur Police">
          {row.policyId === null ? (
            <div className="flex flex-col gap-1 rounded-xl border border-status-warning-border bg-status-warning-bg/40 px-3 py-2.5 text-sm">
              <div className="flex items-center gap-2 text-status-warning">
                <ClipboardList className="size-4 shrink-0" />
                Rechnung nicht zugeordnet
              </div>
              <span className="text-xs text-muted-foreground">
                Offene Aufgabe „Rechnung manuell zuordnen": Zuordnung erfolgt über die Aufgabe.
              </span>
            </div>
          ) : mode === 'none' && confidence !== null && confidence < 90 ? (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-status-warning-border bg-status-warning-bg/40 px-3 py-2.5 text-sm">
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-4 shrink-0 text-status-warning" />
                <span className="text-foreground">
                  Police {row.police}
                  <span className="text-muted-foreground"> · Zuordnung unsicher</span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                {confidence !== null && <KonfidenzBadge value={confidence} label="Konfidenz" />}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={decision.isPending}
                  onClick={() => submit({ kind: 'orchestrate' })}
                >
                  Zuordnung bestätigen
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-card px-3 py-2.5 text-sm">
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-4 shrink-0 text-muted-foreground" />
                <span className="text-foreground">Police {row.police}</span>
              </div>
              <div className="flex items-center gap-2">
                {confidence !== null && <KonfidenzBadge value={confidence} label="Konfidenz" />}
                {row.policyId !== null && onOpenPolice && (
                  <EntityLink onActivate={() => onOpenPolice(row.policyId!)}>Police-Nachweis</EntityLink>
                )}
              </div>
            </div>
          )}
        </Section>

        {/* Prüfsequenz */}
        {checks.length > 0 && (
          <Section title="Prüfsequenz">
            <div className="divide-y divide-border rounded-xl border border-border bg-card px-3">
              {checks.map((c) => (
                <CheckRow key={c.label} tone={c.tone} label={c.label} pflicht={c.pflicht} hint={c.hint} />
              ))}
            </div>
          </Section>
        )}

        {/* Soll-Berechnung expander */}
        {check && (
          <SollBerechnung
            threshold={check.threshold_applied}
            effectiveDate={check.effective_date}
            prorata={check.prorata_applied}
          />
        )}

        {/* Schlussabrechnung — final_settlement only */}
        {settlement && (
          <Section title="Schlussabrechnung · Lohnsumme">
            <div className="flex gap-3">
              <BigValue
                label="Gemeldete Lohnsumme (Soll)"
                value={chf(Number(settlement.reported_wage_sum ?? 0))}
              />
              <BigValue
                label="Deklarierte Lohnsumme (Ist)"
                value={chf(Number(settlement.declared_wage_sum ?? 0))}
              />
            </div>
            <div className="overflow-hidden rounded-xl border border-border bg-card">
              <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 border-b border-border bg-secondary px-3 py-2 text-xs font-medium text-muted-foreground">
                <span>Lohnkategorie</span>
                <span className="text-right">Satz × Lohnsumme</span>
                <span className="text-right">Prämie</span>
              </div>
              {wageCategories.map((c) => (
                <div key={c.category_code} className="grid grid-cols-[1fr_auto_auto] gap-x-4 px-3 py-2 text-sm">
                  <span className="text-foreground">{c.category_code}</span>
                  <span className="mono-num text-right text-muted-foreground">
                    {Number(c.rate).toFixed(2)} % × {chf(Number(c.wage))}
                  </span>
                  <span className="mono-num text-right font-medium">{chf(Number(c.premium))}</span>
                </div>
              ))}
              <div className="flex items-center justify-between border-t border-border px-3 py-2 text-sm">
                <span className="font-medium text-foreground">Definitive Prämie</span>
                <span className="mono-num font-semibold">{chf(Number(settlement.final_premium ?? 0))}</span>
              </div>
              <div className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="text-muted-foreground">− Provisorisch fakturiert</span>
                <span className="mono-num text-muted-foreground">
                  {chf(Number(settlement.provisional_premium ?? 0))}
                </span>
              </div>
              {settlement.balance_amount != null && (
                <div
                  className={cn(
                    'flex items-center justify-between border-t border-border px-3 py-2 text-sm',
                    Number(settlement.balance_amount) >= 0
                      ? 'bg-status-warning-bg/40'
                      : 'bg-status-success-bg/40',
                  )}
                >
                  <span className="font-medium text-foreground">
                    Saldo · {Number(settlement.balance_amount) >= 0 ? 'Nachforderung' : 'Gutschrift'}
                  </span>
                  <span
                    className={cn(
                      'mono-num font-semibold',
                      Number(settlement.balance_amount) >= 0 ? 'text-status-warning' : 'text-status-success',
                    )}
                  >
                    {Number(settlement.balance_amount) >= 0 ? '+' : '−'}
                    {chf(Math.abs(Number(settlement.balance_amount))).replace('CHF ', '')}
                  </span>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Notiz zur Prüfung */}
        {check?.decision_reason && (
          <Section title="Notiz zur Prüfung">
            <p className="rounded-xl border border-border bg-card p-3 text-sm text-foreground">
              {check.decision_reason}
            </p>
          </Section>
        )}

        {/* Verlauf — hidden when history is absent (403/404 or none) */}
        {history && history.activityLog.length > 0 && (
          <Section title="Verlauf">
            <ol className="flex flex-col gap-2">
              {history.activityLog.map((e) => (
                <li key={e.id} className="flex items-start gap-2 text-sm">
                  <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-muted-foreground/50" />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="text-foreground">
                      {e.step}
                      {e.result ? ` · ${e.result}` : ''}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {e.actor} · {dateTimeShortDe(e.createdAt)}
                    </span>
                  </div>
                </li>
              ))}
            </ol>
          </Section>
        )}
      </div>
    </DecisionPeek>
  );
}

/** Soll-Berechnung expander — the applied threshold snapshot + Stichtag/pro-rata. */
function SollBerechnung({
  threshold,
  effectiveDate,
  prorata,
}: {
  threshold: unknown;
  effectiveDate: string | null;
  prorata: boolean | null;
}) {
  const [open, setOpen] = useState(false);
  const t = threshold && typeof threshold === 'object' ? (threshold as Record<string, unknown>) : null;
  return (
    <section className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-sm font-semibold text-foreground"
        aria-expanded={open}
      >
        {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        <CalendarClock className="size-4 text-muted-foreground" />
        Soll-Berechnung
      </button>
      {open && (
        <div className="flex flex-col gap-2 rounded-xl border border-border bg-card p-3 text-sm">
          {t && (
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(t).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-2">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="mono-num text-foreground">{String(v)}</span>
                </div>
              ))}
            </div>
          )}
          {effectiveDate && (
            <div className="flex justify-between gap-2 border-t border-border pt-2">
              <span className="text-muted-foreground">Stichtag</span>
              <span className="text-foreground">
                {dateShortDe(effectiveDate)}
                {prorata ? ' · Pro-rata angewendet' : ''}
              </span>
            </div>
          )}
          {!t && !effectiveDate && (
            <span className="text-xs text-muted-foreground">Keine Berechnungsdaten verfügbar.</span>
          )}
        </div>
      )}
    </section>
  );
}
